import { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Group, Select, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { ResizableDrawer } from '@/components/ResizableDrawer'
import { Callout } from '@/components/ui'
import {
  createApplication, getCatalogoDiretorio, provisionApplication, updateApplication,
  type AtributoCatalogo, type DiretorioSelecao, type SsoApplication, type SsoAppProvisionResult,
} from '@/api/sso'
import { ssoErrorMessage } from './ssoErrors'
import { useFormSimples } from './useFormSimples'
import {
  validaClientId, validaPapelPadrao, validaPostLogoutRedirects, validaRedirects, validaRoleArn,
} from './ssoValidacao'

interface Props {
  opened: boolean
  onClose: () => void
  // Opções do "Cliente atendido" (todos os tenants; o pai busca em /v1/admin/tenants).
  clientes: { value: string; label: string }[]
  app: SsoApplication | null
  // Criação entrega o resultado do create (ou do retry); edição entrega a linha atualizada.
  onSaved: (a: SsoAppProvisionResult | SsoApplication) => void
  // A app existe (rascunho) mesmo que o provision falhe: o pai atualiza a lista.
  onChanged?: () => void
}

interface Values {
  cliente: string
  name: string
  client_id: string
  redirects: string
  post_logout: string
  principal: string
  papel: string
}

// Espelha sso_diretorio_catalogo.FINALIDADE_MIN e DIRETORIO_PADRAO do backend.
const FINALIDADE_MIN = 20
const SEMPRE = 'conta_ativa'
const PADRAO_DIRETORIO = [
  'conta_ativa', 'nome', 'email', 'upn', 'telefones', 'departamento', 'cargo', 'unidade', 'matricula',
]
const ROTULO_ATRIBUTO: Record<string, string> = {
  conta_ativa: 'Conta ativa', cpf: 'CPF', nome: 'Nome completo', nome_proprio: 'Nome próprio', sobrenome: 'Sobrenome',
  email: 'E-mail', upn: 'UPN (login)', telefones: 'Telefones comerciais', celular: 'Celular',
  departamento: 'Departamento', cargo: 'Cargo', unidade: 'Unidade', matricula: 'Matrícula',
}
const rotuloAtributo = (chave: string): string => ROTULO_ATRIBUTO[chave] ?? chave
const finalidadeCurta = (f: string): boolean => f.trim().length < FINALIDADE_MIN

const linhas = (s: string): string[] => s.split('\n').map((l) => l.trim()).filter(Boolean)
const MONO = { input: { fontFamily: 'var(--mantine-font-family-monospace)' } }

export function AplicacaoDrawer({ opened, onClose, clientes, app, onSaved, onChanged }: Props) {
  const edicao = app !== null
  const inicial = (): Values => ({
    cliente: app ? String(app.tenant_id) : '',
    name: app?.name ?? '',
    client_id: app?.keycloak_client_id ?? '',
    redirects: (app?.config.redirect_uris ?? []).join('\n'),
    post_logout: (app?.config.post_logout_redirect_uris ?? []).join('\n'),
    principal: '',
    papel: app?.config.default_role ?? '',
  })
  const form = useFormSimples<Values>(inicial, {
    cliente: (v) => (edicao || v ? null : 'Escolha o cliente atendido.'),
    name: (v) => (edicao || v.trim() ? null : 'Informe o nome da aplicação.'),
    client_id: (v) => (edicao ? null : validaClientId(v)),
    redirects: (v) => validaRedirects(linhas(v)),
    post_logout: (v) => validaPostLogoutRedirects(linhas(v)),
    principal: (v) => (edicao ? null : validaRoleArn(v.trim())),
    papel: (v) => validaPapelPadrao(v.trim()),
  }, opened, app?.id ?? null)
  // Seleção do diretório fora do useFormSimples (que só guarda string). Aplicação criada
  // antes desta tela não tem seleção gravada: só `conta_ativa` é entregue, e a tela diz isso.
  const selecaoInicial = (): DiretorioSelecao => (edicao
    ? {
      atributos: app.config.diretorio?.atributos ?? [SEMPRE],
      finalidades: app.config.diretorio?.finalidades ?? {},
    }
    : { atributos: PADRAO_DIRETORIO, finalidades: {} })
  const [selecao, setSelecao] = useState<DiretorioSelecao>(selecaoInicial)
  const [catalogo, setCatalogo] = useState<AtributoCatalogo[]>([])
  const [erroCatalogo, setErroCatalogo] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  // App criada mas cujo provisionamento falhou: a nova tentativa não cria outra.
  const criadaId = useRef<number | null>(null)
  // Espelho da ref para renderizar: com a app criada, Nome e Client ID deixam de valer.
  const [travado, setTravado] = useState(false)

  useEffect(() => {
    if (!opened) return
    setSelecao(selecaoInicial())
    setErroCatalogo(null)
    let vivo = true
    getCatalogoDiretorio()
      .then((c) => { if (vivo) setCatalogo(c) })
      .catch((e) => { if (vivo) setErroCatalogo(ssoErrorMessage(e).message) })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, app?.id])

  const marcado = (chave: string) => chave === SEMPRE || selecao.atributos.includes(chave)
  const alterna = (chave: string, ligado: boolean) => setSelecao((p) => ({
    ...p,
    atributos: ligado ? [...p.atributos.filter((a) => a !== chave), chave]
      : p.atributos.filter((a) => a !== chave),
  }))
  const setFinalidade = (chave: string, texto: string) => setSelecao((p) => ({
    ...p, finalidades: { ...p.finalidades, [chave]: texto },
  }))
  const pessoais = catalogo.filter((a) => a.pessoal)
  const comuns = catalogo.filter((a) => !a.pessoal)
  const faltaFinalidade = pessoais.some((a) => marcado(a.chave) && finalidadeCurta(selecao.finalidades[a.chave] ?? ''))
  // O que vai no payload: finalidade só dos pessoais marcados, sem espaços nas pontas.
  const selecaoParaEnvio = (): DiretorioSelecao => ({
    // Chave que saiu do catálogo não pode travar a edição: só vai o que o catálogo ainda tem.
    atributos: [...new Set([SEMPRE, ...selecao.atributos])]
      .filter((a) => a === SEMPRE || catalogo.some((c) => c.chave === a)),
    finalidades: Object.fromEntries(
      pessoais.filter((a) => marcado(a.chave)).map((a) => [a.chave, (selecao.finalidades[a.chave] ?? '').trim()])),
  })

  useEffect(() => {
    if (!opened) {
      setErro(null)
      criadaId.current = null
      setTravado(false)
    }
  }, [opened])

  const salvar = async (v: Values) => {
    setErro(null)
    setSalvando(true)
    const redirect_uris = linhas(v.redirects)
    const post_logout_redirect_uris = linhas(v.post_logout)
    const papel = v.papel.trim()
    try {
      if (edicao) {
        // O papel só vai no PUT se mudou: trocar ou limpar mexe no Keycloak de todo mundo.
        const mudouPapel = papel !== (app.config.default_role ?? '')
        // O cliente também só vai se mudou: o backend audita a troca nos dois tenants.
        const mudouCliente = v.cliente !== String(app.tenant_id)
        // A seleção só vai se mudou: o backend audita o antes e o depois.
        const nova = selecaoParaEnvio()
        const antes = selecaoInicial()
        const canonico = (d: DiretorioSelecao) => JSON.stringify([
          [...new Set([SEMPRE, ...d.atributos])].sort(),
          Object.entries(d.finalidades).sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0)),
        ])
        // Catálogo que não carregou: não dá para montar a seleção, e salvar um redirect
        // não pode virar 422 por causa dele.
        const mudouDiretorio = catalogo.length > 0 && canonico(antes) !== canonico(nova)
        const saved = await updateApplication(app.id, {
          redirect_uris, post_logout_redirect_uris,
          ...(mudouDiretorio ? { diretorio: nova } : {}),
          ...(mudouPapel ? { default_role: papel || null } : {}),
          ...(mudouCliente ? { tenant_id: Number(v.cliente) } : {}),
        })
        onSaved(saved)
        onClose()
        return
      }
      let res: SsoAppProvisionResult
      if (criadaId.current === null) {
        // O POST já provisiona: o /provision fica só para a nova tentativa.
        // O realm não vai: é sempre o do provedor da plataforma, decidido no backend.
        res = await createApplication({
          name: v.name.trim(), tenant_id: Number(v.cliente), client_id: v.client_id,
          redirect_uris, post_logout_redirect_uris, secret_consumer_principal: v.principal.trim(),
          ...(papel ? { default_role: papel } : {}),
          diretorio: selecaoParaEnvio(),
        })
        criadaId.current = res.id
        setTravado(true)
        onChanged?.()
      } else {
        // Retry: o que o usuário editou desde a falha vale antes do novo provision.
        await updateApplication(criadaId.current, {
          redirect_uris, post_logout_redirect_uris, default_role: papel || null,
          diretorio: selecaoParaEnvio(),
        })
        res = await provisionApplication(criadaId.current)
      }
      if (res.last_error) {
        // Mesma regra do ssoErrorMessage: o motivo do papel padrão vai para o campo dele.
        const { field } = ssoErrorMessage({ response: { status: 422, data: { detail: res.last_error } } })
        if (field === 'default_role') form.setErroServidor('papel', res.last_error)
        else setErro(res.last_error)
        return
      }
      onSaved(res)
      onClose()
    } catch (e) {
      const { message, field } = ssoErrorMessage(e)
      if (field === 'redirect_uris') form.setErroServidor('redirects', message)
      else if (field === 'post_logout_redirect_uris') form.setErroServidor('post_logout', message)
      else if (field === 'client_id' && !edicao) form.setErroServidor('client_id', message)
      else if (field === 'secret_consumer_principal' && !edicao) form.setErroServidor('principal', message)
      else if (field === 'default_role') form.setErroServidor('papel', message)
      else if (field === 'tenant_id') form.setErroServidor('cliente', message)
      else if (/^atributo /.test(message)) setErroCatalogo(message)
      else setErro(message)
    } finally {
      setSalvando(false)
    }
  }

  // Edição: o cliente atual entra nas opções mesmo se não estiver na lista (ex.: inativo).
  const opcoesCliente = app && !clientes.some((c) => c.value === String(app.tenant_id))
    ? [{ value: String(app.tenant_id), label: app.tenant_name ?? `tenant ${app.tenant_id}` }, ...clientes]
    : clientes

  return (
    <ResizableDrawer
      opened={opened}
      onClose={onClose}
      title={edicao ? 'Aplicação OIDC' : 'Nova aplicação OIDC'}
      storageKey="sso-aplicacao-drawer"
      defaultWidth={560}
    >
      <form onSubmit={(e) => { e.preventDefault(); void salvar(form.values) }}>
        <Stack gap="md">
          {erro && <Callout variant="danger">{erro}</Callout>}
          {edicao ? (
            <>
              <Select
                label="Cliente atendido"
                description="Só muda a coluna Cliente e a trilha de auditoria; o login não depende dele."
                placeholder="Escolha o cliente"
                data={opcoesCliente}
                searchable
                nothingFoundMessage="Nenhum cliente"
                allowDeselect={false}
                value={form.values.cliente || null}
                error={form.props('cliente').error}
                onChange={(v) => form.props('cliente').onChange({ currentTarget: { value: v ?? '' } })}
              />
              <div>
                <Text size="sm" fw={500}>Nome</Text>
                <Text>{app.name}</Text>
              </div>
              <div>
                <Text size="sm" fw={500}>Client ID</Text>
                <Text ff="monospace">{app.keycloak_client_id}</Text>
              </div>
            </>
          ) : (
            <>
              <Select
                label="Cliente atendido"
                placeholder="Escolha o cliente"
                data={clientes}
                searchable
                nothingFoundMessage="Nenhum cliente"
                disabled={travado}
                withAsterisk
                value={form.values.cliente || null}
                error={form.props('cliente').error}
                onChange={(v) => form.props('cliente').onChange({ currentTarget: { value: v ?? '' } })}
              />
              <TextInput label="Nome" disabled={travado} {...form.props('name')} />
              <TextInput label="Client ID" disabled={travado} styles={MONO} {...form.props('client_id')} />
              <TextInput
                label="Role consumidora do segredo"
                description="ARN da role IAM da aplicação que vai ler o client secret no Secrets Manager"
                placeholder="arn:aws:iam::123456789012:role/nome"
                disabled={travado}
                styles={MONO}
                {...form.props('principal')}
              />
            </>
          )}
          <Textarea
            label="Redirects"
            description="Uma URL https por linha"
            autosize minRows={3}
            styles={MONO}
            {...form.props('redirects')}
          />
          <Textarea
            label="URLs pós-logout"
            description="Opcional; uma URL https por linha"
            autosize minRows={2}
            styles={MONO}
            {...form.props('post_logout')}
          />
          <TextInput
            label="Papel padrão (opcional)"
            description="Concedido a todo membro do tenant Entra da conexão (convidados B2B ficam de fora). Vai no claim roles junto com as app roles do Entra. Exige o claim opcional acct no app do Entra: sem ele, ninguém recebe o papel."
            placeholder="ex.: anfitriao"
            styles={MONO}
            {...form.props('papel')}
          />
          <Stack gap="xs">
            <div>
              <Text size="sm" fw={500}>Atributos do diretório</Text>
              <Text size="xs" c="dimmed">
                O que esta aplicação recebe de cada usuário na API de diretório.
                {edicao && !app.config.diretorio && ' Esta aplicação ainda não tem seleção gravada: só conta ativa é entregue.'}
              </Text>
            </div>
            {erroCatalogo && <Callout variant="danger">{erroCatalogo}</Callout>}
            {comuns.map((a) => (
              <Checkbox
                key={a.chave}
                label={rotuloAtributo(a.chave)}
                description={a.chave === SEMPRE ? 'sempre entregue: é o que tira o acesso de quem saiu' : undefined}
                checked={marcado(a.chave)}
                disabled={a.chave === SEMPRE || travado}
                onChange={(e) => alterna(a.chave, e.currentTarget.checked)}
              />
            ))}
            {pessoais.map((a) => (
              <Stack key={a.chave} gap={4}>
                <Checkbox
                  label={`${rotuloAtributo(a.chave)} (dado pessoal)`}
                  description="Nunca fica guardado: é lido no Entra a cada consulta e cada leitura vai para a auditoria."
                  checked={marcado(a.chave)}
                  disabled={travado}
                  onChange={(e) => alterna(a.chave, e.currentTarget.checked)}
                />
                {marcado(a.chave) && (
                  <Textarea
                    label={`Finalidade de ${rotuloAtributo(a.chave)}`}
                    description={`Para que a aplicação precisa deste dado (mínimo ${FINALIDADE_MIN} caracteres)`}
                    autosize minRows={2}
                    value={selecao.finalidades[a.chave] ?? ''}
                    error={finalidadeCurta(selecao.finalidades[a.chave] ?? '')
                      ? `Informe a finalidade, com pelo menos ${FINALIDADE_MIN} caracteres.` : undefined}
                    onChange={(e) => setFinalidade(a.chave, e.currentTarget.value)}
                  />
                )}
              </Stack>
            ))}
          </Stack>
          <Group justify="flex-start">
            <Button type="submit" loading={salvando} disabled={!form.valido || faltaFinalidade}>
              {edicao ? 'Salvar' : 'Criar e provisionar'}
            </Button>
          </Group>
        </Stack>
      </form>
    </ResizableDrawer>
  )
}
